import qs from 'qs'
import {
  AllowedHttpMethods,
  employeeHandbookSettingsApiConfig,
} from '../../apiList'
import {
  AddNewHandbookPage,
  EditPolicyMessageType,
  EmployeeCountry,
  EmployeeHandbook,
  EmployeeHandbookListApiProps,
  EmployeeHandbookListResponse,
  EmployeeHandbookPolicyReportApiProps,
  exportHandbookPolicyReportApiProps,
  HandbookPolicyReportResponse,
  PolicyVersion,
  TotalHandbookList,
  UpdateHandbookPage,
} from '../../../../types/EmployeeHandbook/HandbookSettings/employeeHandbookSettingsTypes'
import {
  getAuthenticatedRequestConfig,
  useAxios,
} from '../../../../utils/apiUtils'
import { ApiParams } from '../../../../types/commonTypes'

const getEmployeeHandbooks = async (
  props: EmployeeHandbookListApiProps,
): Promise<EmployeeHandbookListResponse> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: employeeHandbookSettingsApiConfig.getEmployeeHandbooks,
    method: AllowedHttpMethods.get,
    params: {
      endIndex: props.endIndex ?? 20,
      startIndex: props.startIndex ?? 0,
      country: props.country,
      mandatory: props.mandatory,
      published: props.published,
      search: props.search,
    },
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const getHandbookPolicyReport = async (
  props: EmployeeHandbookPolicyReportApiProps,
): Promise<HandbookPolicyReportResponse> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: employeeHandbookSettingsApiConfig.getHandbookPolicyReport,
    method: AllowedHttpMethods.get,
    params: {
      endIndex: props.endIndex ?? 20,
      startIndex: props.startIndex ?? 0,
      acceptedList: props.acceptedList,
      search: props.search,
      country: props.country,
    },
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const exportHandbookPolicyReport = async (
  props: exportHandbookPolicyReportApiProps,
): Promise<Blob | undefined> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: employeeHandbookSettingsApiConfig.exportHandbookPolicyReport,
    method: AllowedHttpMethods.get,
    params: {
      acceptedList: props.acceptedList,
      search: props.search,
      country: props.country,
    },
    responseType: 'blob',
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const getTotalHandbookList = async (): Promise<TotalHandbookList[]> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: employeeHandbookSettingsApiConfig.getTotalHandbookList,
    method: AllowedHttpMethods.get,
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const deleteEmployeeHandbook = async (
  bookId: number,
): Promise<number | undefined> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: employeeHandbookSettingsApiConfig.deleteEmployeeHandbook,
    method: AllowedHttpMethods.delete,
    params: {
      bookId,
    },
    data: {
      bookId,
    },
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const getEmployeeCountries = async (): Promise<
  EmployeeCountry[] | undefined
> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: employeeHandbookSettingsApiConfig.getEmployeeCountries,
    method: AllowedHttpMethods.get,
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const addNewHandbook = async (
  prepareObject: AddNewHandbookPage,
): Promise<number | undefined> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: employeeHandbookSettingsApiConfig.addNewHandbook,
    method: AllowedHttpMethods.post,
    params: { list: prepareObject.list },
    paramsSerializer: (params: ApiParams) =>
      qs.stringify(params, { arrayFormat: 'repeat' }),
    data: {
      description: prepareObject.description,
      displayOrder: prepareObject.displayOrder,
      pageName: prepareObject.pageName,
      title: prepareObject.title,
      type: 'HandBook',
      isMandatory: prepareObject.isMandatory,
      isAgreed: prepareObject.isAgreed,
      isPublished: prepareObject.isPublished,
      updatedDate: prepareObject.updatedDate,
      version: prepareObject.version,
      oldDate: prepareObject.oldDate,
      isUpdated: false,
      updateContentDate: '',
      oldDateContentDate: '',
    },
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const addHandBookDiscription = async (
  prepareObject: EditPolicyMessageType,
): Promise<void> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: employeeHandbookSettingsApiConfig.addHandBookDiscription,
    method: AllowedHttpMethods.post,
    data: prepareObject,
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const getMessageDescription = async (): Promise<EditPolicyMessageType[]> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: employeeHandbookSettingsApiConfig.getMessageDescription,
    method: AllowedHttpMethods.get,
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const getSelectedCountries = async (id: number): Promise<EmployeeCountry[]> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: employeeHandbookSettingsApiConfig.getSelectedCountries,
    method: AllowedHttpMethods.get,
    params: {
      id,
    },
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const updateEmployeeHandbook = async (
  prepareObject: UpdateHandbookPage,
): Promise<number | undefined> => {
  const { list, ...rest } = prepareObject
  const requestConfig = getAuthenticatedRequestConfig({
    url: employeeHandbookSettingsApiConfig.updateEmployeeHandbook,
    method: AllowedHttpMethods.put,
    params: { list },
    paramsSerializer: (params: ApiParams) =>
      qs.stringify(params, { arrayFormat: 'repeat' }),
    data: {
      ...rest,
      ...{ type: 'HandBook' },
    },
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const getAllPublishedPolicies = async (): Promise<EmployeeHandbook[]> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: employeeHandbookSettingsApiConfig.getPublishedPolicy,
    method: AllowedHttpMethods.get,
  })
  const response = await useAxios(requestConfig)
  return response.data
}
const getPolicyVersion = async (): Promise<PolicyVersion[]> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: employeeHandbookSettingsApiConfig.getVersionPolicy,
    method: AllowedHttpMethods.get,
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const employeeHandbookSettingsApi = {
  getEmployeeHandbooks,
  getTotalHandbookList,
  deleteEmployeeHandbook,
  getEmployeeCountries,
  addNewHandbook,
  getSelectedCountries,
  updateEmployeeHandbook,
  getAllPublishedPolicies,
  getPolicyVersion,
  addHandBookDiscription,
  getMessageDescription,
  getHandbookPolicyReport,
  exportHandbookPolicyReport,
}

export default employeeHandbookSettingsApi
